import apiClient from './api';
import {
  MinutaNueva,
  CrearPedidoNuevoRequest,
  TiposPlatosResponse,
  MinutasNuevasResponse,
  VistaMinutaExcelResponse,
} from '../types/minutasNuevas';

// ============================================================================
// SERVICIOS PARA TIPOS DE PLATOS - VERSIÓN UNIFICADA DINÁMICA
// ============================================================================

class TiposPlato {
  /**
   * GET /api/minutas-nuevas/tipos-platos
   * Obtener todos los tipos de platos desde la tabla tipos_plato de BD
   */
  static async obtenerTodos(): Promise<TiposPlatosResponse> {
    const response = await apiClient.get('/minutas-nuevas/tipos-platos');
    return response.data;
  }
}

// DEPRECATED: Usar TiposPlato en su lugar
class TiposPlatosMinutasAPI {
  /**
   * @deprecated Usar TiposPlato.obtenerTodos() en su lugar
   */
  static async obtenerTodos(): Promise<TiposPlatosResponse> {
    return TiposPlato.obtenerTodos();
  }
}

// ============================================================================
// SERVICIOS PARA MINUTAS NUEVAS
// ============================================================================

class MinutasNuevasAPI {
  /**
   * GET /api/minutas-nuevas
   * Listar minutas con el nuevo sistema
   */
  static async listar(
    filtros: {
      page?: number;
      limit?: number;
      busqueda?: string;
      estado?: string;
      fechaInicio?: string;
      fechaFin?: string;
      // `vigentes=true` activa el filtro de ventana móvil 30d en el
      // backend (minutas en curso o que arrancan en los próximos 30
      // días). `hoy` es la fecha del usuario para evitar TZ del server.
      vigentes?: boolean;
      hoy?: string;
    } = {}
  ): Promise<MinutasNuevasResponse> {
    const params = new URLSearchParams();

    if (filtros.page) params.set('page', filtros.page.toString());
    if (filtros.limit) params.set('limit', filtros.limit.toString());
    if (filtros.busqueda) params.set('busqueda', filtros.busqueda);
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.fechaInicio) params.set('fechaInicio', filtros.fechaInicio);
    if (filtros.fechaFin) params.set('fechaFin', filtros.fechaFin);
    if (filtros.vigentes) params.set('vigentes', 'true');
    if (filtros.hoy) params.set('hoy', filtros.hoy);

    const response = await apiClient.get(
      `/minutas-nuevas?${params.toString()}`
    );
    return response.data;
  }

  /**
   * POST /api/minutas-nuevas
   * Crear una nueva minuta
   */
  static async crear(data: {
    nombre: string;
    descripcion?: string;
    fechaInicio: string;
    fechaFin: string;
    estado?: 'borrador' | 'activa' | 'completada' | 'cancelada';
    // Fix tanda2 T2-05: opt-in para permitir crear una minuta cuyo
    // rango solape con otra activa (tras confirmación explícita del
    // usuario). Sin este flag el backend rechaza con 409.
    forzarSolape?: boolean;
  }): Promise<{ success: boolean; data: MinutaNueva; message: string }> {
    const response = await apiClient.post('/minutas-nuevas', data);
    return response.data;
  }

  /**
   * GET /api/minutas-nuevas/:id/vista-excel
   * Obtener vista tipo Excel de una minuta.
   *
   * Fix tanda2 T2-01 (2026-06-30): el endpoint `/vista-excel` del
   * backend devuelve "Función no implementada aún". El editor real
   * usa `/editar` que devuelve el shape correcto (matriz + estadísticas
   * con completitudPorcentaje alineado al listado). Redirigimos el
   * método a ese endpoint para no romper llamadas existentes.
   */
  static async obtenerVistaExcel(
    minutaId: number
  ): Promise<VistaMinutaExcelResponse> {
    const response = await apiClient.get(`/minutas-nuevas/${minutaId}/editar`);
    return response.data;
  }

  /**
   * GET /api/minutas-nuevas/:id/estadisticas
   * Obtener estadísticas detalladas de una minuta
   */
  static async obtenerEstadisticas(minutaId: number): Promise<{
    success: boolean;
    data: {
      minutasDetalladas: any;
      costos: any;
      porciones: any;
      pedidos: any;
      completitud: any;
    };
  }> {
    const response = await apiClient.get(
      `/minutas-nuevas/${minutaId}/estadisticas`
    );
    return response.data;
  }

  /**
   * PUT /api/minutas-nuevas/:id
   * Actualizar una minuta existente
   */
  static async actualizar(
    minutaId: number,
    data: {
      nombre?: string;
      descripcion?: string;
      estado?: 'borrador' | 'activa' | 'completada' | 'cancelada';
    }
  ): Promise<{ success: boolean; data: MinutaNueva; message: string }> {
    const response = await apiClient.put(`/minutas-nuevas/${minutaId}`, data);
    return response.data;
  }

  /**
   * PUT /api/minutas-nuevas/:id/estado
   * Actualizar el estado de una minuta
   */
  static async actualizarEstado(
    minutaId: number,
    estado: 'borrador' | 'activa' | 'completada' | 'cancelada'
  ): Promise<{ success: boolean; message: string; data: any }> {
    const response = await apiClient.put(`/minutas-nuevas/${minutaId}/estado`, {
      estado,
    });
    return response.data;
  }

  /**
   * DELETE /api/minutas-nuevas/:id
   * Eliminar (desactivar) una minuta
   */
  static async eliminar(
    minutaId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/minutas-nuevas/${minutaId}`);
    return response.data;
  }

  /**
   * POST /api/minutas-nuevas/:id/asignar-receta
   * Asignar una receta a una celda específica (día + tipo de plato)
   */
  static async asignarRecetaMinuta(
    minutaId: number,
    data: {
      fecha_especifica: string;
      tipo_plato_id: number;
      receta_id: number;
      fila_orden?: number;
    }
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await apiClient.post(
      `/minutas-nuevas/${minutaId}/asignar-receta`,
      data
    );
    return response.data;
  }

  /**
   * DELETE /api/minutas-nuevas/:id/remover-receta
   * Remover la asignación de receta de una celda (por minutaDetalladaId)
   */
  static async removerRecetaMinuta(
    minutaId: number,
    minutaDetalladaId: number
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await apiClient.delete(
      `/minutas-nuevas/${minutaId}/remover-receta/${minutaDetalladaId}`
    );
    return response.data;
  }

  /**
   * PUT /api/minutas-nuevas/:id/fechas
   * Actualizar el rango de fechas de una minuta
   */
  static async actualizarFechasMinuta(
    minutaId: number,
    data: {
      fecha_inicio: string;
      fecha_fin: string;
      nombre?: string;
    }
  ): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    recetasEliminadas?: number;
    data?: { recetasAfectadas?: any[] };
  }> {
    const response = await apiClient.put(
      `/minutas-nuevas/${minutaId}/fechas`,
      data
    );
    return response.data;
  }
}

// MinutasDetalladasAPI fue eliminado: los endpoints /minutas-detalladas
// (POST, PUT, DELETE, GET) eran stubs 501 nunca implementados. Toda la
// funcionalidad de asignar/remover/actualizar receta en una celda de
// minuta vive en MinutasNuevasAPI vía /minutas-nuevas/:id/asignar-receta
// y /minutas-nuevas/:id/remover-receta/:detalleId.

// ============================================================================
// SERVICIOS PARA PEDIDOS NUEVOS
// ============================================================================

class PedidosNuevosAPI {
  /**
   * POST /api/pedidos-nuevos
   * Crear pedido con el nuevo sistema
   */
  static async crear(data: CrearPedidoNuevoRequest): Promise<{
    success: boolean;
    data: any;
    message: string;
  }> {
    const response = await apiClient.post('/pedidos-nuevos', data);
    return response.data;
  }

  /**
   * GET /api/pedidos-nuevos
   * Listar pedidos del nuevo sistema
   */
  static async listar(
    filtros: {
      page?: number;
      limit?: number;
      clienteId?: number;
      minutaId?: number;
      estado?: string;
      fechaDesde?: string;
      fechaHasta?: string;
    } = {}
  ): Promise<any> {
    const params = new URLSearchParams();

    if (filtros.page) params.set('page', filtros.page.toString());
    if (filtros.limit) params.set('limit', filtros.limit.toString());
    if (filtros.clienteId)
      params.set('clienteId', filtros.clienteId.toString());
    if (filtros.minutaId) params.set('minutaId', filtros.minutaId.toString());
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.fechaDesde) params.set('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.set('fechaHasta', filtros.fechaHasta);

    const response = await apiClient.get(
      `/pedidos-nuevos?${params.toString()}`
    );
    return response.data;
  }

  /**
   * GET /api/pedidos-nuevos/:id
   * Obtener detalle de un pedido
   */
  static async obtener(pedidoId: number): Promise<any> {
    const response = await apiClient.get(`/pedidos-nuevos/${pedidoId}`);
    return response.data;
  }

  /**
   * PUT /api/pedidos-nuevos/:id
   * Actualizar un pedido
   */
  static async actualizar(pedidoId: number, data: any): Promise<any> {
    const response = await apiClient.put(`/pedidos-nuevos/${pedidoId}`, data);
    return response.data;
  }

  /**
   * PUT /api/pedidos-nuevos/:id/estado
   * Cambiar estado de un pedido
   */
  static async cambiarEstado(pedidoId: number, estado: string): Promise<any> {
    const response = await apiClient.put(`/pedidos-nuevos/${pedidoId}/estado`, {
      estado,
    });
    return response.data;
  }
}

// ============================================================================
// UTILIDADES Y HELPERS
// ============================================================================

class MinutasUtilsAPI {
  /**
   * POST /api/minutas-nuevas/:id/duplicar
   * Duplicar una minuta para otra semana
   */
  static async duplicarMinuta(
    minutaId: number,
    data: {
      nombre: string;
      fechaInicio: string;
      fechaFin: string;
      descripcion?: string;
    }
  ): Promise<{ success: boolean; data: MinutaNueva; message: string }> {
    const response = await apiClient.post(
      `/minutas-nuevas/${minutaId}/duplicar`,
      data
    );
    return response.data;
  }

  /**
   * POST /api/minutas-nuevas/:id/completar-plantilla
   * Completar plantilla de 7x10 para una minuta existente
   */
  static async completarPlantilla(minutaId: number): Promise<{
    success: boolean;
    message: string;
    creadas: number;
  }> {
    const response = await apiClient.post(
      `/minutas-nuevas/${minutaId}/completar-plantilla`
    );
    return response.data;
  }

  /**
   * POST /api/minutas-nuevas/:id/importar-excel
   * Importar minutas desde archivo Excel
   */
  static async importarDesdeExcel(
    minutaId: number,
    archivo: File
  ): Promise<{ success: boolean; message: string; importadas: number }> {
    const formData = new FormData();
    formData.append('archivo', archivo);

    const response = await apiClient.post(
      `/minutas-nuevas/${minutaId}/importar-excel`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * GET /api/minutas-nuevas/:id/exportar-excel
   * Exportar minuta a archivo Excel
   */
  static async exportarAExcel(minutaId: number): Promise<Blob> {
    const response = await apiClient.get(
      `/minutas-nuevas/${minutaId}/exportar-excel`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * POST /api/minutas-nuevas/validar-recetas
   * Validar que todas las recetas de una minuta tengan ingredientes suficientes
   */
  static async validarRecetas(minutaId: number): Promise<{
    success: boolean;
    alertas: {
      recetaId: number;
      recetaNombre: string;
      ingredientesFaltantes: string[];
      porcentajeStock: number;
    }[];
  }> {
    const response = await apiClient.post(`/minutas-nuevas/validar-recetas`, {
      minutaId,
    });
    return response.data;
  }

  /**
   * GET /api/minutas-nuevas/:id/costo-total
   * Calcular costo total estimado de una minuta
   */
  static async calcularCostoTotal(minutaId: number): Promise<{
    success: boolean;
    data: {
      costoTotal: number;
      costoPorDia: { [dia: number]: number };
      costoPorTipo: { [tipo: string]: number };
      porcionesTotales: number;
      costoPorPorcion: number;
    };
  }> {
    const response = await apiClient.get(
      `/minutas-nuevas/${minutaId}/costo-total`
    );
    return response.data;
  }
}

// ============================================================================
// SERVICIO PRINCIPAL UNIFICADO
// ============================================================================

export const minutasNuevasService = {
  // Tipos de platos - VERSIÓN UNIFICADA DINÁMICA
  tiposPlatos: TiposPlato,

  // Minutas (incluye asignación/remoción de recetas en celdas vía
  // MinutasNuevasAPI.asignarRecetaMinuta y removerRecetaMinuta).
  minutas: MinutasNuevasAPI,

  // Pedidos nuevos
  pedidos: PedidosNuevosAPI,

  // Utilidades
  utils: MinutasUtilsAPI,
};

// Exportar servicios individuales también
export {
  TiposPlato,
  TiposPlatosMinutasAPI, // DEPRECATED
  MinutasNuevasAPI,
  PedidosNuevosAPI,
  MinutasUtilsAPI,
};
